import {
    type PossibleTypename,
    JobState,
    Color,
} from '@precon-tools-suite-by-shared/precon-graphql-types';

export type JobRecord<T> = Record<PossibleTypename<'Job'>, T>;

export type JobStateRecord<T> = Record<JobState, T>;

export interface JobStateOptions {
    color: Color;
    shortTitle: string;
    description: string;
    iconClass: string;
}

/**
 * Put all the options for this library that must be set in the `main.ts` of the application here.
 */
export interface PreconJobsGlobalOptions {
    jobTitles?: Partial<JobRecord<string>>;
    defaultTitle?: string;
    jobStates?: Partial<JobStateRecord<Partial<JobStateOptions>>>;
    unknownJobState?: Partial<JobStateOptions>;
}

/**
 * This is the type of the options object used within the library.
 */
export type NormalizedPreconJobsGlobalOptions = {
    jobTitles: JobRecord<string>;
    defaultTitle: string;
    jobStates: JobStateRecord<JobStateOptions>;
    unknownJobState: JobStateOptions;
};

/**
 * Function to convert the input options (`PreconJobsGlobalOptions`) to the normalized
 * options (`NormalizedPreconJobsGlobalOptions`).
 *
 * It usually just sets the default values for the library.
 *
 * @param options The input options as set in the application that uses this library.
 */
export function normalizePreconJobsOptions(
    options: PreconJobsGlobalOptions = {}
): NormalizedPreconJobsGlobalOptions {
    return {
        defaultTitle: 'Achtergrondtaak',
        ...options,
        jobTitles: {
            EchoFilesJob: 'Echo Bestanden',
            ExportAnalysisReportEntriesJob:
                'Exporteer Gevarenanalyse/Verificatieplan',
            ExportSupplierVCardsJob: 'Exporteer Leverancier VCards',
            ExportProductsSpreadsheetJob: 'Exporteer Producten Spreadsheet',
            ExportSupplierBranchesSpreadsheetJob:
                'Exporteer Leveranciers-vestigingen Spreadsheet',
            ExportSuppliersSpreadsheetJob: 'Exporteer Leveranciers Spreadsheet',
            ...(options?.jobTitles ?? {}),
        },
        unknownJobState: {
            color: Color.Gray,
            shortTitle: 'Onbekend',
            description: 'Onbekende status',
            iconClass: 'pi pi-question-circle',
        },
        jobStates: {
            [JobState.Ready]: {
                color: Color.Blue,
                shortTitle: 'Voorbereid',
                description: 'Kan gestart worden',
                iconClass: 'pi pi-play',
            },
            [JobState.Queued]: {
                color: Color.Purple,
                shortTitle: 'Wachtend',
                description: 'Staat in de wachtrij',
                iconClass:
                    'pi pi-hourglass animation-duration-1000 animation-iteration-infinite',
            },
            [JobState.Running]: {
                color: Color.Yellow,
                shortTitle: 'Bezig',
                description: 'Wordt nu verwerkt',
                iconClass: 'pi pi-ellipsis-h',
            },
            [JobState.Expired]: {
                color: Color.Orange,
                shortTitle: 'Verlopen',
                description: 'Heeft te lang in de wachtrij gestaan',
                iconClass: 'pi pi-exclamation-circle',
            },
            [JobState.Failed]: {
                color: Color.Red,
                shortTitle: 'Fout',
                description: 'Fout bij verwerken',
                iconClass: 'pi pi-times-circle',
            },
            [JobState.Cancelled]: {
                color: Color.Bluegray,
                shortTitle: 'Gestopt',
                description: 'Geannuleerd door een persoon',
                iconClass: 'pi pi-stop',
            },
            [JobState.Finished]: {
                color: Color.Green,
                shortTitle: 'Klaar',
                description: 'Successvol verwerkt',
                iconClass: 'pi pi-check-circle',
            },
        },
    };
}
